import { FolderOpen, Video, Image as ImageIcon, Heart, Users } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils'
import type { AssetFilterType } from './asset-filters'

interface AssetEmptyStateProps {
  filter: AssetFilterType
  onNavigate: (path: string) => void
  fullPage?: boolean
}

const config: Record<AssetFilterType, { 
  icon: React.ElementType
  title: string
  description: string
  action?: { label: string; href: string } 
}> = {
  all: {
    icon: FolderOpen,
    title: 'No assets yet',
    description: 'Your generated videos and images will appear here. Create your first video to get started!',
    action: { label: 'Create Your First Video', href: '/video' },
  },
  video: {
    icon: Video,
    title: 'No videos yet',
    description: "You haven't generated any videos yet. Create your first AI video!",
    action: { label: 'Create a Video', href: '/video' },
  },
  image: {
    icon: ImageIcon,
    title: 'No images yet',
    description: "You haven't generated any images yet. Create your first AI image!",
    action: { label: 'Create an Image', href: '/images' },
  },
  favorites: {
    icon: Heart,
    title: 'No favorites yet',
    description: "You haven't favorited any assets yet. Click the heart icon on any asset to add it to your favorites.",
  },
  // Rendered on the Assets > Characters tab via CharacterGrid (which
  // forwards through to AssetEmptyState for visual parity with the rest
  // of the Assets page empty states).
  characters: {
    icon: Users,
    title: 'No characters yet',
    description: 'Your characters will appear here. Create your first character to get started!',
    action: { label: 'Create Your First Character', href: '/characters' },
  },
}

export function AssetEmptyState({ filter, onNavigate, fullPage = false }: AssetEmptyStateProps) {
  const { icon: Icon, title, description, action } = config[filter]

  return (
    <div className={cn(
      "flex flex-col items-center justify-center text-center px-4",
      fullPage ? "min-h-[60vh]" : "min-h-[40vh]"
    )}>
      <div className={cn("rounded-xl glass-elevated", fullPage ? "p-6 mb-6" : "p-5 mb-5")}>
        <Icon className={cn("text-[#666]", fullPage ? "size-12" : "size-10")} />
      </div>
      <h2 className={cn("font-semibold text-white mb-2", fullPage ? "text-2xl" : "text-xl")}>
        {title}
      </h2>
      <p className={cn("text-[#666] max-w-md", fullPage ? "mb-6" : "mb-5")}>
        {description}
      </p>
      {action && (
        <Button variant="glass-active" onClick={() => onNavigate(action.href)}>
          {action.label}
        </Button>
      )}
    </div>
  )
}
